import { KV_LIVE_STATS_UNLEASH_FLAG, getDeltaForKey } from '@ahaslides-product/frontend-toolkit'
import mqttClient from './mqtt.client'
import config from '@/config'
import store from '@/vuex/store'
import { captureException } from '@sentry/vue'

const SLIDE_TYPES = {
  WORD_CLOUD: 'WORD_CLOUD',
  POLL: 'POLL',
}
const MESSAGE_DELIMITER = '*|*'

const STATS_TYPES = {
  ANSWERER_COUNT: 'ANSWERER_COUNT',
  ANSWER_COUNT: 'ANSWER_COUNT',
  AVERAGE_SCORE: 'AVERAGE_SCORE',
  CORRECT_ANSWER_COUNT: 'CORRECT_ANSWER_COUNT',
  INCORRECT_ANSWER_COUNT: 'INCORRECT_ANSWER_COUNT',
  REACTION_COUNT: 'REACTION_COUNT',
  LAST_REACTION: 'LAST_REACTION',
  AVERAGE_TEAM_SCORE: 'AVERAGE_TEAM_SCORE',
  TOTAL_TEAM_SCORE: 'TOTAL_TEAM_SCORE',
  FASTEST_TEAM_SCORE: 'FASTEST_TEAM_SCORE',
}

const STATS_TYPES_INDEX = [
  STATS_TYPES.ANSWER_COUNT,
  STATS_TYPES.ANSWERER_COUNT,
  STATS_TYPES.AVERAGE_SCORE,
  STATS_TYPES.CORRECT_ANSWER_COUNT,
  STATS_TYPES.INCORRECT_ANSWER_COUNT,
]

const AGGREGATION_LEVELS = {
  PER_ACTIVITY: 'PER_ACTIVITY',
  PER_QUESTION: 'PER_QUESTION',
  PER_ANSWER_VALUE: 'PER_ANSWER_VALUE',
  PER_LIVE_SESSION: 'PER_LIVE_SESSION',
  PER_REACTION_TYPE: 'PER_REACTION_TYPE',
}

const AGGREGATION_LEVELS_INDEX = [
  AGGREGATION_LEVELS.PER_ANSWER_VALUE,
  AGGREGATION_LEVELS.PER_QUESTION,
  AGGREGATION_LEVELS.PER_ACTIVITY,
  AGGREGATION_LEVELS.PER_LIVE_SESSION,
]

export const Topics = {
  answerCountPerAnswerValueTopic(activityId, questionId, version, answerValue) {
    return `${STATS_TYPES.ANSWER_COUNT}/${AGGREGATION_LEVELS.PER_ANSWER_VALUE}/${activityId}/${version}/${questionId}/${answerValue}`
  },

  answerCountPerActivityTopic(activityId, version) {
    return `${STATS_TYPES.ANSWER_COUNT}/${AGGREGATION_LEVELS.PER_ACTIVITY}/${activityId}/${version}`
  },

  averageScorePerQuestionTopic(activityId, version, questionId) {
    return `${STATS_TYPES.AVERAGE_SCORE}/${AGGREGATION_LEVELS.PER_QUESTION}/${activityId}/${version}/${questionId}`
  },

  averageScorePerActivityTopic(activityId, version) {
    return `${STATS_TYPES.AVERAGE_SCORE}/${AGGREGATION_LEVELS.PER_ACTIVITY}/${activityId}/${version}`
  },

  answererCountPerQuestionTopic(activityId, questionId, version) {
    return `${STATS_TYPES.ANSWERER_COUNT}/${AGGREGATION_LEVELS.PER_QUESTION}/${activityId}/${version}/${questionId}`
  },

  reactionCountPerTypeTopic(liveSessionId, version, reactionType) {
    return `${STATS_TYPES.REACTION_COUNT}/${AGGREGATION_LEVELS.PER_REACTION_TYPE}/${liveSessionId}/${version}/${reactionType}`
  },

  correctAnswerCountPerAnswerValueTopic(activityId, version, questionId, answerValue) {
    return `${STATS_TYPES.CORRECT_ANSWER_COUNT}/${AGGREGATION_LEVELS.PER_ANSWER_VALUE}/${activityId}/${version}/${questionId}/${answerValue}`
  },

  incorrectAnswerCountPerAnswerValueTopic(activityId, version, questionId, answerValue) {
    return `${STATS_TYPES.INCORRECT_ANSWER_COUNT}/${AGGREGATION_LEVELS.PER_ANSWER_VALUE}/${activityId}/${version}/${questionId}/${answerValue}`
  },

  correctAnswerCountPerActivityTopic(activityId, version) {
    return `${STATS_TYPES.CORRECT_ANSWER_COUNT}/${AGGREGATION_LEVELS.PER_ACTIVITY}/${activityId}/${version}`
  },

  incorrectAnswerCountPerActivityTopic(activityId, version) {
    return `${STATS_TYPES.INCORRECT_ANSWER_COUNT}/${AGGREGATION_LEVELS.PER_ACTIVITY}/${activityId}/${version}`
  },
}

const statsGetter = {
  [`${STATS_TYPES.ANSWER_COUNT}_${AGGREGATION_LEVELS.PER_ACTIVITY}`]: (arr, message) => {
    return {
      type: `${STATS_TYPES.ANSWER_COUNT}_${AGGREGATION_LEVELS.PER_ACTIVITY}`,
      identifier: Number(arr[0]),
      version: Number(arr[1]),
      answerCount: Number(message),
    }
  },
  [`${STATS_TYPES.ANSWER_COUNT}_${AGGREGATION_LEVELS.PER_ANSWER_VALUE}`]: (arr, message) => {
    return {
      type: `${STATS_TYPES.ANSWER_COUNT}_${AGGREGATION_LEVELS.PER_ANSWER_VALUE}`,
      identifier: Number(arr[0]),
      version: Number(arr[1]),
      questionId: arr[2],
      answerValue: arr[3],
      answerCount: Number(message),
    }
  },
  [`${STATS_TYPES.ANSWERER_COUNT}_${AGGREGATION_LEVELS.PER_QUESTION}`]: (arr, message) => {
    return {
      type: `${STATS_TYPES.ANSWER_COUNT}_${AGGREGATION_LEVELS.PER_ANSWER_VALUE}`,
      identifier: Number(arr[0]),
      version: Number(arr[1]),
      questionId: arr[2],
      answererCount: Number(message),
    }
  },
  [`${STATS_TYPES.AVERAGE_SCORE}_${AGGREGATION_LEVELS.PER_QUESTION}`]: (arr, message) => {
    return {
      type: `${STATS_TYPES.AVERAGE_SCORE}_${AGGREGATION_LEVELS.PER_QUESTION}`,
      identifier: Number(arr[0]),
      version: Number(arr[1]),
      questionId: Number(arr[2]),
      averageScore: message,
    }
  },
  [`${STATS_TYPES.AVERAGE_SCORE}_${AGGREGATION_LEVELS.PER_ACTIVITY}`]: (arr, message) => {
    return {
      type: `${STATS_TYPES.AVERAGE_SCORE}_${AGGREGATION_LEVELS.PER_ACTIVITY}`,
      identifier: Number(arr[0]),
      version: Number(arr[1]),
      averageScore: message,
    }
  },
  [`${STATS_TYPES.CORRECT_ANSWER_COUNT}_${AGGREGATION_LEVELS.PER_ANSWER_VALUE}`]: (arr, message) => {
    return {
      type: `${STATS_TYPES.CORRECT_ANSWER_COUNT}_${AGGREGATION_LEVELS.PER_ANSWER_VALUE}`,
      identifier: Number(arr[0]),
      version: Number(arr[1]),
      questionId: arr[2],
      answerValue: arr[3],
      answerCount: message,
    }
  },
  [`${STATS_TYPES.INCORRECT_ANSWER_COUNT}_${AGGREGATION_LEVELS.PER_ANSWER_VALUE}`]: (arr, message) => {
    return {
      type: `${STATS_TYPES.INCORRECT_ANSWER_COUNT}_${AGGREGATION_LEVELS.PER_ANSWER_VALUE}`,
      identifier: Number(arr[0]),
      version: Number(arr[1]),
      questionId: arr[2],
      answerValue: arr[3],
      answerCount: message,
    }
  },
  [`${STATS_TYPES.CORRECT_ANSWER_COUNT}_${AGGREGATION_LEVELS.PER_ACTIVITY}`]: (arr, message) => {
    return {
      type: `${STATS_TYPES.CORRECT_ANSWER_COUNT}_${AGGREGATION_LEVELS.PER_ACTIVITY}`,
      identifier: Number(arr[0]),
      version: Number(arr[1]),
      answerCount: message,
    }
  },
  [`${STATS_TYPES.INCORRECT_ANSWER_COUNT}_${AGGREGATION_LEVELS.PER_ACTIVITY}`]: (arr, message) => {
    return {
      type: `${STATS_TYPES.INCORRECT_ANSWER_COUNT}_${AGGREGATION_LEVELS.PER_ACTIVITY}`,
      identifier: Number(arr[0]),
      version: Number(arr[1]),
      answerCount: message,
    }
  },
  [`${STATS_TYPES.REACTION_COUNT}_${AGGREGATION_LEVELS.PER_REACTION_TYPE}`]: (arr, message) => {
    return {
      type: `${STATS_TYPES.REACTION_COUNT}_${AGGREGATION_LEVELS.PER_REACTION_TYPE}`,
      identifier: Number(arr[0]),
      version: Number(arr[1]),
      reactionType: arr[2],
      reactionCount: Number(message),
    }
  },
  [`${STATS_TYPES.LAST_REACTION}_${AGGREGATION_LEVELS.PER_LIVE_SESSION}`]: (arr, message) => {
    const reactionInfo = message.split(MESSAGE_DELIMITER)
    return {
      type: `${STATS_TYPES.LAST_REACTION}_${AGGREGATION_LEVELS.PER_LIVE_SESSION}`,
      identifier: Number(arr[0]),
      version: Number(arr[1]),
      reactionType: reactionInfo[0],
      audienceName: reactionInfo[1],
    }
  },

}

const statsGettersV2 = {
  ...statsGetter,
  [`${STATS_TYPES.ANSWER_COUNT}_${AGGREGATION_LEVELS.PER_ANSWER_VALUE}`]: (arr, message) => {
    return {
      type: `${STATS_TYPES.ANSWER_COUNT}_${AGGREGATION_LEVELS.PER_ANSWER_VALUE}`,
      identifier: Number(arr[0]),
      version: Number(arr[1]),
      questionId: arr[2],
      answerValue: decodeURIComponent(arr[3]),
      answerCount: Number(message),
    }
  },
  [`${STATS_TYPES.CORRECT_ANSWER_COUNT}_${AGGREGATION_LEVELS.PER_ANSWER_VALUE}`]: (arr, message) => {
    return {
      type: `${STATS_TYPES.CORRECT_ANSWER_COUNT}_${AGGREGATION_LEVELS.PER_ANSWER_VALUE}`,
      identifier: Number(arr[0]),
      version: Number(arr[1]),
      questionId: arr[2],
      answerValue: decodeURIComponent(arr[3]),
      answerCount: message,
    }
  },
  [`${STATS_TYPES.INCORRECT_ANSWER_COUNT}_${AGGREGATION_LEVELS.PER_ANSWER_VALUE}`]: (arr, message) => {
    return {
      type: `${STATS_TYPES.INCORRECT_ANSWER_COUNT}_${AGGREGATION_LEVELS.PER_ANSWER_VALUE}`,
      identifier: Number(arr[0]),
      version: Number(arr[1]),
      questionId: arr[2],
      answerValue: decodeURIComponent(arr[3]),
      answerCount: message,
    }
  },
}

function getStatParser(statsType, aggregationLevel) {
  if (store.getters[KV_LIVE_STATS_UNLEASH_FLAG]) {
    return statsGettersV2[`${statsType}_${aggregationLevel}`]
  }
  return statsGetter[`${statsType}_${aggregationLevel}`]
}

const getCallbackKey = ({ statsType, aggregationLevel, identifier }) => {
  return `${statsType}_${aggregationLevel}_${identifier}`
}

const addDeltaToLiveStatEvent = ({ message, topic }) => {
  try {
    const delta = getDeltaForKey(topic)
    if (delta) {
      return Number(message.toString()) + delta
    }
  } catch (err) {
    captureException(err)
  }
  return message
}

class LiveStats {
  constructor() {
    this.callbacks = {}
  }

  _getCallbacks({ identifier, statsType, aggregationLevel }) {
    if (!identifier || !statsType || !aggregationLevel) return []
    const key = getCallbackKey({ identifier, statsType, aggregationLevel })
    if (!this.callbacks[key]) {
      return []
    }
    return this.callbacks[key]
  }

  connect() {
    mqttClient.on('message', (topic, message) => {
      let arr = topic.split('/')

      // trim the v2 prefix if it exists
      if (arr[1] === store.getters['liveStatsV2/liveStatsVersionPrefix']) {
        arr = arr.slice(1)
      }

      const statsType = arr[2]
      const aggregationLevel = arr[3]
      const topicWithoutPrefix = arr.slice(2).join('/')
      const statParser = getStatParser(statsType, aggregationLevel)
      if (typeof statParser != 'function') {
        return
      }
      if (store.getters['liveStatsV2/canAddDeltaForAhaSync']) {
        message = addDeltaToLiveStatEvent({ topic, message })
      }
      const statsUpdate = statParser(arr.slice(4), message.toString())

      const callbacks = this._getCallbacks({
        statsType,
        aggregationLevel,
        identifier: statsUpdate.identifier,
      })
      callbacks.forEach((callback) => {
        callback(topicWithoutPrefix, message.toString(), statsUpdate)
      })
    })
  }

  _addCallback({ statsType, aggregationLevel, identifier }, callback) {
    if (!identifier || !statsType) {
      return
    }
    const key = getCallbackKey({ statsType, aggregationLevel, identifier })
    if (!this.callbacks[key]) {
      this.callbacks[key] = []
    }

    this.callbacks[key].push(callback)
  }

  _removeCallbacks({ statsType, aggregationLevel, identifier }, callback) {
    if (!identifier || !statsType) {
      return
    }
    const callbacks = this._getCallbacks({ statsType, aggregationLevel, identifier })
    if (callback) {
      const idx = callbacks.indexOf(callback)
      if (idx > -1) {
        callbacks.splice(idx, 1)
      }
    } else {
      delete this.callbacks[getCallbackKey({ statsType, aggregationLevel, identifier })]
    }
  }

  subscribeStats({
    statsType, aggregationLevel, identifier,
  }, callback) {
    let topic = `/${config.livestatsTopicPrefix}/${statsType}/${aggregationLevel}/${identifier}/#`
    if (store.getters[KV_LIVE_STATS_UNLEASH_FLAG]) {
      topic = `/${store.getters['liveStatsV2/liveStatsVersionPrefix']}${topic}`
    }
    this._addCallback({ statsType, aggregationLevel, identifier }, callback)
    mqttClient.subscribe(topic)
  }

  unsubscribeStats({
    identifier, statsType, aggregationLevel,
  }, callback) {
    let topic = `/${config.livestatsTopicPrefix}/${statsType}/${aggregationLevel}/${identifier}/#`
    if (store.getters[KV_LIVE_STATS_UNLEASH_FLAG]) {
      topic = `/${store.getters['liveStatsV2/liveStatsVersionPrefix']}${topic}`
    }
    this._removeCallbacks({ statsType, aggregationLevel, identifier }, callback)
    mqttClient.unsubscribe(topic)
  }
}

const liveStats = new LiveStats()
// The Stats index is a mapping from raw data which returned from BE.
// Currently, I don't use that yet, but put it for future integration.
export {
  liveStats,
  SLIDE_TYPES,
  STATS_TYPES,
  AGGREGATION_LEVELS,
  STATS_TYPES_INDEX,
  AGGREGATION_LEVELS_INDEX,
}
